// Surface-scoped _document markup: which folded surfaces need parser-blocking
// document scripts, and how _document learns that for a given request.
//
// WHY A COOKIE, NOT A PAGE PROP: _document renders before/outside any page, and
// getInitialProps there has no access to the page component's props. The proxy
// is the one place that already knows the route class, so it stamps a tiny
// non-identity signal cookie on document responses for surfaces that need
// scripts (first: conversions-manager's CreatorNav/Transcend; then homepage's
// MetaTags/TagManager/Transcend). The cookie is strictly request-scoped signal
// — no PII, no identity, not read by the app — and pages that never set it
// render the same markup as before the fold.

export const SURFACE_DOCUMENT_SCRIPTS_COOKIE_NAME = '__eb_web_surface_doc';

/** Request header the signal cookie rides in on (case as sent by the proxy). */
export const SURFACE_DOCUMENT_SCRIPTS_COOKIE_HEADER = 'x-eb-surface-doc-scripts';

/**
 * The `EB_LANG` request header value, flattened the way Node exposes repeated
 * headers, so _document can derive the Transcend locale and `<html lang>`.
 */
export function getSurfaceDocumentLocale(
  value: string | string[] | undefined,
): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw && raw.length > 0 ? raw : null;
}

/**
 * The Set-Cookie the proxy emits for a surface whose _document needs scripts.
 * `value` is the surface signal ('event-nav' for conversions-manager,
 * 'homepage' for the folded homepage), so _document needs no per-surface table
 * of its own.
 */
export function surfaceDocumentScriptsCookie(value: string, pathPrefix: string): string {
  return `${SURFACE_DOCUMENT_SCRIPTS_COOKIE_NAME}=${encodeURIComponent(value)}; Path=${pathPrefix}; SameSite=Lax`;
}

/** The request-header value the proxy forwards so the CURRENT request's
 * _document sees the signal (a Set-Cookie only reaches _document on the next
 * visit). _document parses the value with the cookie parser (same lookup as
 * the browser-cookie path), so the header must carry a cookie-pair string. */
export function surfaceDocumentScriptsCookieValue(value: string): string {
  return `${SURFACE_DOCUMENT_SCRIPTS_COOKIE_NAME}=${encodeURIComponent(value)}`;
}

/**
 * Discriminated surface signal. `event-nav` is conversions-manager's
 * creator-nav resource name (kept byte-identical to before homepage folded) and
 * doubles as its document signal; `homepage` is the homepage's; `listings` is
 * the listings event-page's (its Transcend airgap in _document + GTM
 * preconnect / URL.parse polyfill).
 */
export type SurfaceDocumentSignal =
  | 'event-nav'
  | 'homepage'
  | 'listings'
  | 'event-dashboard';

export const CONVERSIONS_MANAGER_SURFACE_DOCUMENT_PREFIX = '/conversions-manager';
export const HOMEPAGE_SURFACE_DOCUMENT_PREFIX = '/home';
export const HOMEPAGE_API_PREFIX = '/home/api';

export const EVENT_DASHBOARD_INTERNAL_PREFIX = '/event-dashboard';
export const EVENT_DASHBOARD_PUBLIC_PREFIX = '/event-dashboard-webapp';
export const EVENT_DASHBOARD_API_PREFIX = '/api/event-dashboard';
export const EVENT_DASHBOARD_LEGACY_API_PREFIX = '/event-dashboard-webapp/api';

export function isEventDashboardDocumentPath(pathname: string): boolean {
  if (
    !matchesPrefix(pathname, EVENT_DASHBOARD_INTERNAL_PREFIX) &&
    !matchesPrefix(pathname, EVENT_DASHBOARD_PUBLIC_PREFIX)
  ) {
    return false;
  }
  return (
    !matchesPrefix(pathname, EVENT_DASHBOARD_API_PREFIX) &&
    !matchesPrefix(pathname, EVENT_DASHBOARD_LEGACY_API_PREFIX)
  );
}

export function isEventDashboardSurfacePath(pathname: string): boolean {
  return (
    matchesPrefix(pathname, EVENT_DASHBOARD_INTERNAL_PREFIX) ||
    matchesPrefix(pathname, EVENT_DASHBOARD_PUBLIC_PREFIX) ||
    matchesPrefix(pathname, EVENT_DASHBOARD_API_PREFIX)
  );
}

export function isEventDashboardDocumentAsPath(asPath: unknown): boolean {
  if (typeof asPath !== 'string') {
    return false;
  }
  const pathOnly = asPath.split('?')[0].split('#')[0];
  return isEventDashboardDocumentPath(pathOnly);
}
/**
 * The listings surface's internal document mount. The public entry is `/e`
 * (country TLDs + `/e/<slug>`); the traffic seam or the next.config owner maps
 * the public form onto this internal mount, which is what the host proxy sees
 * when it stamps the document signal. Both are listed so the signal follows the
 * request whichever spelling reaches the proxy.
 */
export const LISTINGS_SURFACE_DOCUMENT_PREFIX = '/listings';
export const LISTINGS_PUBLIC_DOCUMENT_PREFIX = '/e';
export const LISTINGS_API_PREFIX = '/e/api';

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Document paths that need the creator-nav document scripts. */
export const SURFACE_DOCUMENT_SCRIPTS_PREFIXES = ['/conversions-manager'] as const;

/**
 * Resolve which surface's document scripts a request path needs. Homepage API
 * requests must NOT carry the document signal, so `/home/api/**` is excluded
 * from the homepage prefix here.
 */
export function resolveSurfaceDocumentSignal(
  pathname: string,
): SurfaceDocumentSignal | null {
  if (matchesPrefix(pathname, CONVERSIONS_MANAGER_SURFACE_DOCUMENT_PREFIX)) {
    return 'event-nav';
  }
  if (
    matchesPrefix(pathname, HOMEPAGE_SURFACE_DOCUMENT_PREFIX) &&
    !matchesPrefix(pathname, HOMEPAGE_API_PREFIX)
  ) {
    return 'homepage';
  }
  // Both the internal mount and the public `/e` form, but never the BFF.
  if (
    (matchesPrefix(pathname, LISTINGS_SURFACE_DOCUMENT_PREFIX) ||
      matchesPrefix(pathname, LISTINGS_PUBLIC_DOCUMENT_PREFIX)) &&
    !matchesPrefix(pathname, LISTINGS_API_PREFIX)
  ) {
    return 'listings';
  }
  if (isEventDashboardDocumentPath(pathname)) {
    return 'event-dashboard';
  }
  return null;
}

/** The path prefix the signal cookie is scoped to for a given surface. */
export function surfaceDocumentPathPrefix(signal: SurfaceDocumentSignal): string {
  switch (signal) {
    case 'homepage':
      return HOMEPAGE_SURFACE_DOCUMENT_PREFIX;
    case 'listings':
      return LISTINGS_PUBLIC_DOCUMENT_PREFIX;
    case 'event-dashboard':
      return EVENT_DASHBOARD_PUBLIC_PREFIX;
    default:
      return CONVERSIONS_MANAGER_SURFACE_DOCUMENT_PREFIX;
  }
}

export function isSurfaceDocumentScriptsPath(pathname: string): boolean {
  return resolveSurfaceDocumentSignal(pathname) !== null;
}

/**
 * Any path owned by the folded homepage surface: its documents (`/home`,
 * `/home/[locale]`, `/home/it-lists/**`) AND its BFF (`/home/api/**`). Distinct
 * from `isSurfaceDocumentScriptsPath`, which deliberately excludes the API
 * namespace. Used by the proxy to re-stamp the trusted edge cohort header on
 * every homepage request (not just the CM BFF) so the surface's telemetry can
 * carry `cohort`.
 */
export function isHomepageSurfacePath(pathname: string): boolean {
  return matchesPrefix(pathname, HOMEPAGE_SURFACE_DOCUMENT_PREFIX)
    || matchesPrefix(pathname, '/api/home');
}

/**
 * Any path owned by the folded listings surface: its documents (the internal
 * `/listings` mount and the public `/e` entry) AND its BFF in either spelling
 * (`/e/api/**` and the canonical `/api/listings/**`). Distinct from
 * `isSurfaceDocumentScriptsPath`, which deliberately excludes the API
 * namespace. Used by the proxy to re-stamp the trusted edge cohort header on
 * every listings request so the surface's telemetry can carry `cohort`.
 */
export function isListingsSurfacePath(pathname: string): boolean {
  return matchesPrefix(pathname, LISTINGS_SURFACE_DOCUMENT_PREFIX)
    || matchesPrefix(pathname, LISTINGS_PUBLIC_DOCUMENT_PREFIX)
    || matchesPrefix(pathname, '/api/listings');
}
